/**
 * Language and country of a provider category, read from its name.
 *
 * Providers encode them in many ways: "ENGLISH UK | NEWS", "ARAB | MOROCCO",
 * "UK| SPORTS", "FRENCH: CINEMA", "DRAMA [FR]", "مسلسلات مغربية [AR]". This
 * recognises the common forms; anything it cannot place stays untagged and is
 * still shown when no filter is chosen.
 */
export interface CategoryTags {
  language: string | null;
  country: string | null;
}

interface Tag {
  language?: string;
  country?: string;
}

const MULTI = 'Multi-language';
const NORDIC = 'Nordic languages';

/** Short codes, as used in "[FR]" tags and "FR |" prefixes. */
const CODES: Record<string, Tag> = {
  EN: { language: 'English' },
  ENG: { language: 'English' },
  UK: { language: 'English', country: 'United Kingdom' },
  GB: { language: 'English', country: 'United Kingdom' },
  US: { language: 'English', country: 'USA' },
  USA: { language: 'English', country: 'USA' },
  CA: { country: 'Canada' },
  AU: { language: 'English', country: 'Australia' },
  IE: { language: 'English', country: 'Ireland' },
  FR: { language: 'French' },
  FRA: { language: 'French', country: 'France' },
  QC: { language: 'French', country: 'Canada' },
  BE: { country: 'Belgium' },
  CH: { country: 'Switzerland' },
  ES: { language: 'Spanish' },
  ESP: { language: 'Spanish', country: 'Spain' },
  LAT: { language: 'Spanish', country: 'Latin America' },
  MX: { language: 'Spanish', country: 'Mexico' },
  AR: { language: 'Arabic' },
  ARA: { language: 'Arabic' },
  DE: { language: 'German' },
  GER: { language: 'German', country: 'Germany' },
  AT: { language: 'German', country: 'Austria' },
  IT: { language: 'Italian' },
  ITA: { language: 'Italian', country: 'Italy' },
  PT: { language: 'Portuguese' },
  BR: { language: 'Portuguese', country: 'Brazil' },
  NL: { language: 'Dutch', country: 'Netherlands' },
  TR: { language: 'Turkish', country: 'Turkey' },
  TUR: { language: 'Turkish', country: 'Turkey' },
  IN: { language: 'Hindi', country: 'India' },
  PK: { language: 'Urdu', country: 'Pakistan' },
  IR: { language: 'Persian', country: 'Iran' },
  FA: { language: 'Persian' },
  SE: { language: 'Swedish', country: 'Sweden' },
  SWE: { language: 'Swedish', country: 'Sweden' },
  DK: { language: 'Danish', country: 'Denmark' },
  NO: { language: 'Norwegian', country: 'Norway' },
  FI: { language: 'Finnish', country: 'Finland' },
  SCA: { language: NORDIC },
  '4K-SCA': { language: NORDIC },
  PL: { language: 'Polish', country: 'Poland' },
  RO: { language: 'Romanian', country: 'Romania' },
  RU: { language: 'Russian', country: 'Russia' },
  GR: { language: 'Greek', country: 'Greece' },
  AL: { language: 'Albanian', country: 'Albania' },
  EX: { language: 'Serbo-Croatian', country: 'Ex-Yugoslavia' },
  MA: { country: 'Morocco' },
  DZ: { country: 'Algeria' },
  TN: { country: 'Tunisia' },
  EG: { country: 'Egypt' },
  AF: { country: 'Africa' },
  KSA: { language: 'Arabic', country: 'Saudi Arabia' },
  UAE: { language: 'Arabic', country: 'UAE' },
  MULTI: { language: MULTI },
};

/** Whole words or phrases (upper case, accents removed). Longer phrases win over shorter ones. */
const WORDS: Record<string, Tag> = {
  ENGLISH: { language: 'English' },
  UK: { language: 'English', country: 'United Kingdom' },
  'UNITED KINGDOM': { language: 'English', country: 'United Kingdom' },
  BRITISH: { language: 'English', country: 'United Kingdom' },
  'UNITED STATES': { language: 'English', country: 'USA' },
  AMERICAN: { language: 'English', country: 'USA' },
  CANADA: { language: 'English', country: 'Canada' },
  CANADIAN: { language: 'English', country: 'Canada' },
  AUSTRALIA: { language: 'English', country: 'Australia' },
  IRELAND: { language: 'English', country: 'Ireland' },
  IRISH: { language: 'English', country: 'Ireland' },
  FRENCH: { language: 'French' },
  FRANCAIS: { language: 'French' },
  FRANCE: { language: 'French', country: 'France' },
  QUEBEC: { language: 'French', country: 'Canada' },
  BELGIUM: { country: 'Belgium' },
  BELGIQUE: { country: 'Belgium' },
  SWITZERLAND: { country: 'Switzerland' },
  SUISSE: { country: 'Switzerland' },
  SPAIN: { language: 'Spanish', country: 'Spain' },
  ESPANA: { language: 'Spanish', country: 'Spain' },
  SPANISH: { language: 'Spanish' },
  ESPANOL: { language: 'Spanish' },
  LATINO: { language: 'Spanish', country: 'Latin America' },
  LATIN: { language: 'Spanish', country: 'Latin America' },
  MEXICO: { language: 'Spanish', country: 'Mexico' },
  ARGENTINA: { language: 'Spanish', country: 'Argentina' },
  PERU: { language: 'Spanish', country: 'Peru' },
  CHILE: { language: 'Spanish', country: 'Chile' },
  COLOMBIA: { language: 'Spanish', country: 'Colombia' },
  VENEZUELA: { language: 'Spanish', country: 'Venezuela' },
  VENEZULA: { language: 'Spanish', country: 'Venezuela' },
  ECUADOR: { language: 'Spanish', country: 'Ecuador' },
  URUGUAY: { language: 'Spanish', country: 'Uruguay' },
  PANAMA: { language: 'Spanish', country: 'Panama' },
  HONDURAS: { language: 'Spanish', country: 'Honduras' },
  'COSTA RICA': { language: 'Spanish', country: 'Costa Rica' },
  NICARAGUA: { language: 'Spanish', country: 'Nicaragua' },
  GUATEMALA: { language: 'Spanish', country: 'Guatemala' },
  'PUERTO RICO': { language: 'Spanish', country: 'Puerto Rico' },
  'EL SALVADOR': { language: 'Spanish', country: 'El Salvador' },
  'R. DOMINICANA': { language: 'Spanish', country: 'Dominican Republic' },
  DOMINICANA: { language: 'Spanish', country: 'Dominican Republic' },
  BRASIL: { language: 'Portuguese', country: 'Brazil' },
  BRAZIL: { language: 'Portuguese', country: 'Brazil' },
  PORTUGAL: { language: 'Portuguese', country: 'Portugal' },
  PORTUGUESE: { language: 'Portuguese' },
  GERMANY: { language: 'German', country: 'Germany' },
  GERMAN: { language: 'German' },
  DEUTSCH: { language: 'German' },
  DEUTSCHLAND: { language: 'German', country: 'Germany' },
  AUSTRIA: { language: 'German', country: 'Austria' },
  ITALY: { language: 'Italian', country: 'Italy' },
  ITALIA: { language: 'Italian', country: 'Italy' },
  ITALIAN: { language: 'Italian' },
  NETHERLANDS: { language: 'Dutch', country: 'Netherlands' },
  DUTCH: { language: 'Dutch' },
  TURKEY: { language: 'Turkish', country: 'Turkey' },
  TURKISH: { language: 'Turkish', country: 'Turkey' },
  INDIA: { country: 'India' },
  HINDI: { language: 'Hindi', country: 'India' },
  URDU: { language: 'Urdu', country: 'Pakistan' },
  PAKISTAN: { language: 'Urdu', country: 'Pakistan' },
  PERSIAN: { language: 'Persian', country: 'Iran' },
  IRAN: { language: 'Persian', country: 'Iran' },
  KOREAN: { language: 'Korean', country: 'South Korea' },
  KOREA: { language: 'Korean', country: 'South Korea' },
  SWEDEN: { language: 'Swedish', country: 'Sweden' },
  SWEDISH: { language: 'Swedish', country: 'Sweden' },
  NORWAY: { language: 'Norwegian', country: 'Norway' },
  NORSK: { language: 'Norwegian', country: 'Norway' },
  DENMARK: { language: 'Danish', country: 'Denmark' },
  DANSKE: { language: 'Danish', country: 'Denmark' },
  FINLAND: { language: 'Finnish', country: 'Finland' },
  POLAND: { language: 'Polish', country: 'Poland' },
  ROMANIA: { language: 'Romanian', country: 'Romania' },
  RUSSIA: { language: 'Russian', country: 'Russia' },
  GREECE: { language: 'Greek', country: 'Greece' },
  ALBANIA: { language: 'Albanian', country: 'Albania' },
  ARAB: { language: 'Arabic' },
  ARABIC: { language: 'Arabic' },
  'ARAB-VIP': { language: 'Arabic' },
  MOROCCO: { language: 'Arabic', country: 'Morocco' },
  MAROC: { language: 'Arabic', country: 'Morocco' },
  ALGERIA: { language: 'Arabic', country: 'Algeria' },
  ALGERIE: { language: 'Arabic', country: 'Algeria' },
  TUNISIA: { language: 'Arabic', country: 'Tunisia' },
  TUNISIE: { language: 'Arabic', country: 'Tunisia' },
  LIBYA: { language: 'Arabic', country: 'Libya' },
  EGYPT: { language: 'Arabic', country: 'Egypt' },
  EGYPTE: { language: 'Arabic', country: 'Egypt' },
  MAURITANIA: { language: 'Arabic', country: 'Mauritania' },
  SUDAN: { language: 'Arabic', country: 'Sudan' },
  LEBANON: { language: 'Arabic', country: 'Lebanon' },
  LIBAN: { language: 'Arabic', country: 'Lebanon' },
  SYRIA: { language: 'Arabic', country: 'Syria' },
  JORDAN: { language: 'Arabic', country: 'Jordan' },
  PALESTINE: { language: 'Arabic', country: 'Palestine' },
  PALASTIN: { language: 'Arabic', country: 'Palestine' },
  IRAQ: { language: 'Arabic', country: 'Iraq' },
  KUWAIT: { language: 'Arabic', country: 'Kuwait' },
  QATAR: { language: 'Arabic', country: 'Qatar' },
  BAHRAIN: { language: 'Arabic', country: 'Bahrain' },
  OMAN: { language: 'Arabic', country: 'Oman' },
  YEMEN: { language: 'Arabic', country: 'Yemen' },
  KSA: { language: 'Arabic', country: 'Saudi Arabia' },
  SAUDI: { language: 'Arabic', country: 'Saudi Arabia' },
  UAE: { language: 'Arabic', country: 'UAE' },
  AFRICAN: { country: 'Africa' },
  AFRICA: { country: 'Africa' },
};

/** Arabic-script country words (matched as substrings: they take prefixes and suffixes). */
const ARABIC_WORDS: [string, string][] = [
  ['مغرب', 'Morocco'],
  ['أمازيغ', 'Morocco'],
  ['جزائر', 'Algeria'],
  ['تونس', 'Tunisia'],
  ['مصر', 'Egypt'],
  ['سوري', 'Syria'],
  ['لبنان', 'Lebanon'],
  ['عراق', 'Iraq'],
  ['خليج', 'Gulf'],
  ['ترك', 'Turkey'],
  ['هندي', 'India'],
  ['إيران', 'Iran'],
];

/** Words naming a language: they override the language of the marker ("TURKISH ENSUB [EN]" is Turkish). */
const LANGUAGE_WORDS = new Set(['ENGLISH', 'FRENCH', 'FRANCAIS', 'SPANISH', 'ESPANOL', 'PORTUGUESE', 'GERMAN', 'DEUTSCH', 'ITALIAN', 'DUTCH', 'TURKISH', 'HINDI', 'URDU', 'PERSIAN', 'KOREAN', 'SWEDISH', 'ARABIC']);

const PHRASES = Object.keys(WORDS).sort((a, b) => b.length - a.length);

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase();
}

function wordIn(text: string, phrase: string): boolean {
  const at = text.indexOf(phrase);
  if (at < 0) return false;
  const before = at === 0 ? ' ' : text.charAt(at - 1);
  const after = at + phrase.length >= text.length ? ' ' : text.charAt(at + phrase.length);
  return !/[A-Z0-9]/.test(before) && !/[A-Z0-9]/.test(after);
}

/** The prefix before the first separator ("ENGLISH UK | NEWS" → "ENGLISH UK"), if there is one. */
function prefixOf(text: string): string | null {
  // A leading bar is allowed: "|DE| FILME 1990-2023".
  const match = /^\s*\|?\s*([^|:[\]]{1,24}?)\s*[|:]/.exec(text);
  return match?.[1] ? match[1].trim() : null;
}

function tagFor(token: string): Tag | undefined {
  return CODES[token] ?? WORDS[token] ?? (token.startsWith('ENGLISH ') ? { ...WORDS[token.slice(8)], language: 'English', ...CODES[token.slice(8)] } : undefined);
}

export function categoryTags(name: string): CategoryTags {
  const text = normalize(name);
  let language: string | null = null;
  let country: string | null = null;
  const apply = (tag: Tag | undefined) => {
    if (tag?.language) language = tag.language;
    if (tag?.country) country = tag.country;
  };

  // 1. The explicit marker: "[FR]" at the end, else the "XX |" prefix.
  const bracket = /\[([^\]]+)\]\s*$/.exec(text);
  const prefix = prefixOf(text);
  const prefixTag = prefix === null ? undefined : tagFor(prefix);
  apply(prefixTag);
  if (bracket?.[1]) apply(tagFor(bracket[1].trim()));

  // 2. Words in the rest of the name refine the country ("ARAB | MOROCCO",
  //    "LATINO | MEXICO") and the language ("CANADA | FRENCH", "TURKISH ENSUB [EN]").
  let rest = bracket ? text.slice(0, bracket.index) : text;
  if (prefix !== null && prefixTag) rest = rest.slice(rest.indexOf(prefix) + prefix.length);
  let found: string | null = null;
  let placed = false;
  for (const phrase of PHRASES) {
    // Skip phrases inside a longer one already found ("LATIN" in "LATINO").
    if (!wordIn(rest, phrase) || (found !== null && found.includes(phrase))) continue;
    const tag = WORDS[phrase];
    if (!tag) continue;
    if (tag.country && !placed) {
      country = tag.country;
      placed = true;
    }
    if (tag.language && (!language || language === MULTI || language === NORDIC || LANGUAGE_WORDS.has(phrase))) language = tag.language;
    found ??= phrase;
  }
  if (!country) {
    for (const [word, place] of ARABIC_WORDS) {
      if (name.includes(word)) {
        country = place;
        break;
      }
    }
  }
  return { language, country };
}
